/**
 * Vendor Navigation Bar Web Component
 * Supports Hover on desktop and Tap/Click toggle on desktop & mobile/touch devices.
 * Features viewport collision detection to prevent menu overflow.
 */
if (!customElements.get('vendor-navigation-bar')) {
  class VendorNavigationBar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    connectedCallback() {
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onResize = this.handleResize.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('resize', this.onResize);
    }

    init() {
      const dropdownItems = this.querySelectorAll('.vendor-nav__item.has-dropdown');
      dropdownItems.forEach((item) => {
        const toggleBtn = item.querySelector('.vendor-nav__toggle');
        const mainLink = item.querySelector('.vendor-nav__link');

        // Toggle on button click
        if (toggleBtn) {
          toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleDropdown(item, toggleBtn);
          });
        }

        // If main link has no URL or is '#', clicking link also toggles dropdown
        if (mainLink) {
          const href = mainLink.getAttribute('href');
          if (!href || href === '#' || href === 'javascript:void(0);' || href === 'javascript:void(0)') {
            mainLink.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.toggleDropdown(item, toggleBtn);
            });
          }
        }

        // Desktop hover accessibility & positioning
        item.addEventListener('mouseenter', () => {
          if (window.matchMedia('(hover: hover)').matches) {
            item.classList.add('is-hovered');
            if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
            this.adjustItemPositions(item);
          }
        });

        item.addEventListener('mouseleave', () => {
          if (window.matchMedia('(hover: hover)').matches) {
            item.classList.remove('is-hovered');
            if (!item.classList.contains('is-open')) {
              if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
            }
          }
        });
      });

      // Handle Grandchild Dropdowns
      const grandchildItems = this.querySelectorAll('.vendor-nav__dropdown-item.has-grandchild-dropdown');
      grandchildItems.forEach((gItem) => {
        const gToggleBtn = gItem.querySelector('.vendor-nav__subtoggle');
        const gSublink = gItem.querySelector('.vendor-nav__sublink');

        if (gToggleBtn) {
          gToggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleGrandchild(gItem, gToggleBtn);
          });
        }

        if (gSublink) {
          const href = gSublink.getAttribute('href');
          if (!href || href === '#' || href === 'javascript:void(0);' || href === 'javascript:void(0)') {
            gSublink.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.toggleGrandchild(gItem, gToggleBtn);
            });
          }
        }

        gItem.addEventListener('mouseenter', () => {
          if (window.matchMedia('(hover: hover)').matches) {
            this.adjustGrandchildPosition(gItem);
          }
        });
      });
    }

    /**
     * Viewport collision detection for parent dropdown and its children
     */
    adjustItemPositions(item) {
      const dropdown = item.querySelector('.vendor-nav__dropdown');
      if (!dropdown) return;

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const rect = dropdown.getBoundingClientRect();

      // Check if level-1 dropdown overflows right side of viewport
      if (rect.right > viewportWidth - 16) {
        dropdown.classList.add('vendor-nav__dropdown--align-right');
      } else if (rect.left < 16 && dropdown.classList.contains('vendor-nav__dropdown--align-right')) {
        dropdown.classList.remove('vendor-nav__dropdown--align-right');
      }

      // Check all grandchild dropdowns
      const grandchildItems = item.querySelectorAll('.vendor-nav__dropdown-item.has-grandchild-dropdown');
      grandchildItems.forEach((gItem) => {
        this.adjustGrandchildPosition(gItem);
      });
    }

    /**
     * Viewport collision detection for grandchild flyout
     */
    adjustGrandchildPosition(gItem) {
      const gDropdown = gItem.querySelector('.vendor-nav__grandchild-dropdown');
      if (!gDropdown) return;

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const parentDropdown = gItem.closest('.vendor-nav__dropdown');
      
      if (parentDropdown) {
        const parentRect = parentDropdown.getBoundingClientRect();
        const gWidth = gDropdown.offsetWidth || 220;

        // If opening to the right would overflow viewport, flip leftward
        if (parentRect.right + gWidth > viewportWidth - 16 || parentDropdown.classList.contains('vendor-nav__dropdown--align-right')) {
          gDropdown.classList.add('vendor-nav__grandchild-dropdown--left');
        } else {
          gDropdown.classList.remove('vendor-nav__grandchild-dropdown--left');
        }
      }
    }

    /**
     * Toggle parent dropdown
     */
    toggleDropdown(item, toggleBtn) {
      const isOpen = item.classList.contains('is-open');

      // Close other open dropdowns at top level
      this.querySelectorAll('.vendor-nav__item.is-open').forEach((otherItem) => {
        if (otherItem !== item) {
          otherItem.classList.remove('is-open');
          const otherBtn = otherItem.querySelector('.vendor-nav__toggle');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        }
      });

      if (isOpen) {
        item.classList.remove('is-open');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('is-open');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
        this.adjustItemPositions(item);
      }
    }

    /**
     * Toggle grandchild dropdown
     */
    toggleGrandchild(gItem, gToggleBtn) {
      const isGOpen = gItem.classList.contains('is-open');

      // Close sibling grandchild items
      const parentDropdown = gItem.closest('.vendor-nav__dropdown');
      if (parentDropdown) {
        parentDropdown.querySelectorAll('.vendor-nav__dropdown-item.is-open').forEach((sibling) => {
          if (sibling !== gItem) {
            sibling.classList.remove('is-open');
            const siblingBtn = sibling.querySelector('.vendor-nav__subtoggle');
            if (siblingBtn) siblingBtn.setAttribute('aria-expanded', 'false');
          }
        });
      }

      if (isGOpen) {
        gItem.classList.remove('is-open');
        if (gToggleBtn) gToggleBtn.setAttribute('aria-expanded', 'false');
      } else {
        gItem.classList.add('is-open');
        if (gToggleBtn) gToggleBtn.setAttribute('aria-expanded', 'true');
        this.adjustGrandchildPosition(gItem);
      }
    }

    /**
     * Handle resize
     */
    handleResize() {
      this.querySelectorAll('.vendor-nav__item.has-dropdown').forEach((item) => {
        this.adjustItemPositions(item);
      });
    }

    /**
     * Close all dropdowns when clicking outside
     */
    handleDocumentClick(e) {
      if (!this.contains(e.target)) {
        this.closeAll();
      }
    }

    /**
     * Close on Escape key press
     */
    handleKeyDown(e) {
      if (e.key === 'Escape') {
        this.closeAll();
      }
    }

    closeAll() {
      this.querySelectorAll('.vendor-nav__item.is-open').forEach((item) => {
        item.classList.remove('is-open');
        const btn = item.querySelector('.vendor-nav__toggle');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });

      this.querySelectorAll('.vendor-nav__dropdown-item.is-open').forEach((gItem) => {
        gItem.classList.remove('is-open');
        const gBtn = gItem.querySelector('.vendor-nav__subtoggle');
        if (gBtn) gBtn.setAttribute('aria-expanded', 'false');
      });
    }
  }

  customElements.define('vendor-navigation-bar', VendorNavigationBar);
}
